import React from 'react';
import styled from 'styled-components';
import SkeletonBlock from '../../../../components/SkeletonBlock';

/** Layout-aware skeleton blocks matching the loaded panel shapes. */

const Card = styled.div`
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 22px 26px;
  margin-bottom: 16px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
`;

const HeroCard = styled(Card)`
  padding: 20px 56px 20px 24px;
`;

const Row = styled.div<{ $gap?: number }>`
  display: flex;
  align-items: center;
  gap: ${({ $gap }) => $gap ?? 12}px;
`;

const Stack = styled.div<{ $gap?: number }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $gap }) => $gap ?? 8}px;
`;

const Grid = styled.div<{ $cols: number; $gap?: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $cols }) => $cols}, 1fr);
  gap: ${({ $gap }) => $gap ?? 12}px;
`;

const StageRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 32px;
  padding: 8px 4px;
`;

const StageCell = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  min-width: 110px;
`;

const StageDot = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(90deg, #f2f2f2 25%, #e6e6e6 37%, #f2f2f2 63%);
  background-size: 400% 100%;
  animation: shimmer 1.4s ease infinite;

  @keyframes shimmer {
    0% { background-position: 100% 50%; }
    100% { background-position: 0 50%; }
  }
`;

// ---------------------------------------------------------------------------
// HeroHeader
// ---------------------------------------------------------------------------

export const HeroHeaderSkeleton: React.FC = () => (
  <HeroCard>
    <Row $gap={12} style={{ marginBottom: 10 }}>
      <SkeletonBlock height={22} width={220} />
      <SkeletonBlock height={20} width={72} radius={11} />
    </Row>
    <Row $gap={16}>
      <SkeletonBlock height={12} width={170} />
      <SkeletonBlock height={12} width={130} />
    </Row>
  </HeroCard>
);

// ---------------------------------------------------------------------------
// StatStrip — 4 tiles row
// ---------------------------------------------------------------------------

export const StatStripSkeleton: React.FC<{ tiles?: number }> = ({ tiles = 4 }) => (
  <Grid $cols={tiles} style={{ marginBottom: 16 }}>
    {Array.from({ length: tiles }).map((_, i) => (
      <Card key={i} style={{ padding: '14px 16px', marginBottom: 0, minHeight: 92 }}>
        <Stack $gap={8}>
          <SkeletonBlock height={11} width={70} />
          <SkeletonBlock height={20} width='60%' />
          <SkeletonBlock height={11} width='40%' />
        </Stack>
      </Card>
    ))}
  </Grid>
);

// ---------------------------------------------------------------------------
// Section wrapper — title + custom body
// ---------------------------------------------------------------------------

export const SectionSkeleton: React.FC<{
  titleWidth?: number;
  children: React.ReactNode;
}> = ({ titleWidth = 90, children }) => (
  <Card>
    <SkeletonBlock height={13} width={titleWidth} style={{ marginBottom: 16 }} />
    {children}
  </Card>
);

// ---------------------------------------------------------------------------
// Horizontal stage rail
// ---------------------------------------------------------------------------

export const StageRailSkeleton: React.FC<{ stages?: number }> = ({ stages = 5 }) => (
  <StageRow>
    {Array.from({ length: stages }).map((_, i) => (
      <StageCell key={i}>
        <StageDot />
        <SkeletonBlock height={11} width={70} />
        <SkeletonBlock height={10} width={50} />
      </StageCell>
    ))}
  </StageRow>
);

// ---------------------------------------------------------------------------
// Run-history table — header row + N body rows
// ---------------------------------------------------------------------------

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 80px 160px 80px 1fr;
  gap: 16px;
  padding: 12px 4px;
  border-bottom: 1px solid #fafafa;
  align-items: center;
`;

const TableHeaderRow = styled(TableRow)`
  border-bottom: 1px solid #f0f0f0;
  background: #fafafa;
  padding: 10px 4px;
`;

export const RunHistoryTableSkeleton: React.FC<{ rows?: number }> = ({ rows = 5 }) => (
  <div>
    <TableHeaderRow>
      {[60, 50, 100, 60, 90].map((w, i) => (
        <SkeletonBlock key={i} height={10} width={w} />
      ))}
    </TableHeaderRow>
    {Array.from({ length: rows }).map((_, i) => (
      <TableRow key={i}>
        <SkeletonBlock height={13} width='80%' />
        <SkeletonBlock height={20} width={70} radius={11} />
        <SkeletonBlock height={12} width='90%' />
        <SkeletonBlock height={12} width={50} />
        <SkeletonBlock height={12} width='75%' />
      </TableRow>
    ))}
  </div>
);

// ---------------------------------------------------------------------------
// Tab strip — tab heads then table
// ---------------------------------------------------------------------------

export const TabbedRunsSkeleton: React.FC<{ tabs?: number; rows?: number }> = ({
  tabs = 2,
  rows = 4
}) => (
  <div>
    <Row $gap={16} style={{ marginBottom: 16, borderBottom: '1px solid #f0f0f0', paddingBottom: 10 }}>
      {Array.from({ length: tabs }).map((_, i) => (
        <SkeletonBlock key={i} height={12} width={120} />
      ))}
    </Row>
    <StatStripSkeleton tiles={2} />
    <RunHistoryTableSkeleton rows={rows} />
  </div>
);

// ---------------------------------------------------------------------------
// Section header (Recent runs + last-30-days badge)
// ---------------------------------------------------------------------------

export const SectionHeaderSkeleton: React.FC = () => (
  <Row style={{ justifyContent: 'space-between', marginBottom: 14 }}>
    <SkeletonBlock height={13} width={90} />
    <SkeletonBlock height={11} width={90} />
  </Row>
);

// ---------------------------------------------------------------------------
// Pipeline card (used in grid) — top row, badges, footer
// ---------------------------------------------------------------------------

const CardShell = styled.div`
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 132px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
`;

export const PipelineCardSkeleton: React.FC = () => (
  <CardShell>
    <Row style={{ justifyContent: 'space-between' }}>
      <SkeletonBlock height={20} width={70} radius={11} />
      <SkeletonBlock height={18} width={50} radius={9} />
    </Row>
    <Stack $gap={6}>
      <SkeletonBlock height={15} width='75%' />
      <SkeletonBlock height={11} width='50%' />
    </Stack>
    <Row style={{ marginTop: 'auto', justifyContent: 'space-between' }}>
      <SkeletonBlock height={11} width={100} />
      <SkeletonBlock height={11} width={40} />
    </Row>
  </CardShell>
);

// ---------------------------------------------------------------------------
// Group section header (e.g. "ETL · 4")
// ---------------------------------------------------------------------------

export const GroupTitleSkeleton: React.FC = () => (
  <Row $gap={10}>
    <SkeletonBlock height={13} width={100} />
    <SkeletonBlock height={16} width={28} radius={10} />
  </Row>
);

// ---------------------------------------------------------------------------
// Timer card
// ---------------------------------------------------------------------------

export const TimerCardSkeleton: React.FC = () => (
  <Card style={{ padding: 28 }}>
    <Stack $gap={8} style={{ marginBottom: 20 }}>
      <SkeletonBlock height={22} width={220} />
      <SkeletonBlock height={12} width={150} />
    </Stack>
    <Grid $cols={2} $gap={16}>
      {Array.from({ length: 4 }).map((_, i) => (
        <Card key={i} style={{ background: '#fafafa', padding: '14px 16px', marginBottom: 0 }}>
          <Stack $gap={8}>
            <SkeletonBlock height={11} width={80} />
            <SkeletonBlock height={14} width='70%' />
          </Stack>
        </Card>
      ))}
    </Grid>
  </Card>
);
